import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../../../layouts';
import Page from '../../../../../components/Page';
import axios from '../../../../../utils/axios';
import { fDateSimple } from '../../../../../utils/formatTime';
import { formatBytes } from '../../../../../utils';
import { HistoricoGPS } from '../../../../../components/historicoGPS';
import GoogleMap from '../../../../../components/GoogleMaps/GoogleMap';
import GoogleMarker from '../../../../../components/GoogleMaps/GoogleMarker';
import { Num, CABECALHO_TABELA_COMPACTO, CartaoIndicador, semAcento } from '../../../../../components/gps';
import MenuAcoes from '../../../../../components/gps/MenuAcoes';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../../../../components/gps/Modal';
import { ResumoExclusao } from '../../../../../components/gps/MenuAcoes';
import ModalEditarDispositivo from '../../../../../components/gps/ModalEditarDispositivo';
import ModalMudarGrupo from '../../../../../components/gps/ModalMudarGrupo';
import ModalMudarSenha from '../../../../../components/gps/ModalMudarSenha';
import ModalPerdaRoubo from '../../../../../components/gps/ModalPerdaRoubo';
import ModalGeolocalizacao from '../../../../../components/gps/ModalGeolocalizacao';
import ModalHistoricoDispositivo from '../../../../../components/gps/ModalHistoricoDispositivo';

/* ============================================================================
   Detalhe do dispositivo - visual novo (shadcn + Tailwind).

   Rota paralela; a tela antiga segue em /dashboard/dispositivos/:id/item/:deviceId.

   TODAS as funcoes da tela antiga estao aqui - as seis acoes de aparelho, as seis
   abas, o mapa, a copia do DEVICE_ID e o "limpar dados" por aplicativo. Nada foi
   retirado. Os mesmos endpoints, os mesmos corpos de requisicao.

   QUATRO MUDANCAS, cada uma por um motivo:

   1. AS ACOES DEIXARAM DE SER SEIS ICONES IGUAIS EM FILA. Na tela antiga
      "Reiniciar" e "Reset de fabrica" eram dois botoes de icone, do mesmo tamanho,
      um do lado do outro - e reset de fabrica APAGA O APARELHO. Agora as acoes de
      consulta (sincronizar, localizar, tocar) sao botoes com rotulo, e as duas
      destrutivas ficam separadas, em vermelho, atras de confirmacao que nomeia o
      aparelho.

   2. SWEETALERT2 SAIU, o Modal do padrao novo entrou. Mesmas perguntas, mesmos
      textos de confirmacao, mesmo campo de senha no recadastro.

   3. OS 20 BLOQUEIOS DA POLITICA PASSARAM A APARECER. A tela antiga MONTAVA essa
      lista (`setBloqueios`, ~20 campos da politica) e nunca a renderizava - era
      estado morto. O dado ja vinha na resposta; agora e uma aba. Isto e adicao,
      nao troca: nada deixou de ser mostrado.

   4. O NUMERO DE SERIE, IMEI e DEVICE_ID viraram mono tabular e copiaveis - sao
      os campos que o suporte dita por telefone.

   Contrato: GET /dispositivos/device/:deviceId -> { device, aplicativos, logDevice }
   ============================================================================ */

type Bloqueio = { label: string; value: string };
type ItemRelatorio = { displayName?: string; packageName?: string; versionName?: string; versionCode?: string };
type ConsumoApp = { nome?: string; packageName?: string; consumo_dados_moveis?: number; consumo_wifi?: number };

const TIPO_APLICATIVO: Record<string, string> = {
  INSTALL_TYPE_UNSPECIFIED: 'Sem especificar',
  PREINSTALLED: 'Pre-instalado',
  FORCE_INSTALLED: 'Forçar instalação',
  BLOCKED: 'Bloqueado',
  AVAILABLE: 'Disponivel para download',
  REQUIRED_FOR_SETUP: 'Obrigatorio na instalação',
  KIOSK: 'Modo Kiosk',
};

const ABAS = [
  { id: 'info', rotulo: 'Informações' },
  { id: 'detalhes', rotulo: 'Outros detalhes' },
  { id: 'apps', rotulo: 'Aplicativos (do usuário)' },
  { id: 'sistema', rotulo: 'Aplicativos de sistema' },
  { id: 'consumo', rotulo: 'Consumo de dados' },
  { id: 'bloqueios', rotulo: 'Bloqueios da política' },
  { id: 'historico', rotulo: 'Históricos' },
];

function texto(v: unknown) {
  const s = v === null || v === undefined || v === '' ? '' : String(v);
  return s || '—';
}

function bytes(v: unknown) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return formatBytes(n);
  } catch {
    return '—';
  }
}

DetalheDispositivo.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

/* Glifo de traco dos cartoes. Um componente so, com o desenho vindo por prop:
   seis <svg> repetidos inteiros seriam trinta linhas de moldura identica para
   seis `d` diferentes. */
function Glifo({ d }: { d: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d={d} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function DetalheDispositivo() {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const deviceId = String(router.query.deviceId ?? '');
  const grupoId = String(router.query.id ?? '-1');

  const [device, setDevice] = useState<any>(null);
  const [aplicativos, setAplicativos] = useState<any[]>([]);
  const [logDevice, setLogDevice] = useState<any>(null);
  const [bloqueios, setBloqueios] = useState<Bloqueio[]>([]);
  const [relatorio, setRelatorio] = useState<ItemRelatorio[]>([]);
  const [consumos, setConsumos] = useState<ConsumoApp[]>([]);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState('');
  const [aba, setAba] = useState('info');

  /* PESQUISA DAS TRES ABAS DE LISTA (10/09/2026, a pedido).

     Um estado so para as tres (Aplicativos, Sistema, Consumo), zerado ao trocar
     de aba. Estados separados guardariam um filtro que a pessoa nao ve: ela sai
     de "Aplicativos" filtrando por "gps", volta meia hora depois e encontra uma
     lista curta sem lembrar por que. Zerar e o comportamento previsivel.

     Filtra no cliente porque as tres listas ja vieram inteiras na resposta do
     dispositivo - nao ha requisicao a fazer. */
  const [buscaAba, setBuscaAba] = useState('');

  const [lat, setLat] = useState<any>(null);
  const [lng, setLng] = useState<any>(null);
  const [zoom, setZoom] = useState(17);
  const [address, setAddress] = useState('');

  // Confirmacoes
  const [menuAcoes, setMenuAcoes] = useState(false);
  const [confirmarReiniciar, setConfirmarReiniciar] = useState(false);
  const [confirmarReset, setConfirmarReset] = useState(false);
  const [recadastrar, setRecadastrar] = useState(false);

  /* AS SETE ACOES QUE SO EXISTIAM NO MENU DA LISTA (20/08/2026).

     O detalhe tinha as acoes de COMANDO (sincronizar, localizar, tocar,
     recadastrar, reiniciar, reset) e nenhuma das de CADASTRO - editar, mudar
     grupo, mudar senha, perda/roubo, geolocalizacao, historico de logs. Para
     qualquer uma delas era preciso voltar para a lista, achar a linha do mesmo
     aparelho e abrir o menu: navegacao para trabalhar no aparelho que ja esta
     aberto na tela.

     Os modais sao OS MESMOS componentes que a lista usa - nenhum foi duplicado.
     `onSalvo={carregarTudo}` porque o que eles gravam aparece nesta tela (nome,
     CR, grupo, senha, status de perda), e sem recarregar a tela mostraria o valor
     antigo logo depois de salvar. */
  const [abrirEditar, setAbrirEditar] = useState(false);
  const [abrirGrupo, setAbrirGrupo] = useState(false);
  const [abrirSenha, setAbrirSenha] = useState(false);
  const [abrirPerda, setAbrirPerda] = useState(false);
  const [abrirGeo, setAbrirGeo] = useState(false);
  const [abrirLogs, setAbrirLogs] = useState(false);
  const [senhaRecadastro, setSenhaRecadastro] = useState('');
  const [limparApp, setLimparApp] = useState<any>(null);

  const podeAgir = Number(user?.perfil ?? 0) > 0;

  const carregar = useCallback(async () => {
    if (!deviceId) return;
    setCarregando(true);
    setErro('');
    try {
      const { data } = await axios.get<any>(`/dispositivos/device/${encodeURIComponent(deviceId)}`);
      setDevice(data?.device ?? null);
      setAplicativos(data?.aplicativos ?? []);
      setLogDevice(data?.logDevice ?? null);

      if (data?.logDevice?.latitude) setLat(Number(data.logDevice.latitude));
      if (data?.logDevice?.longitude) setLng(Number(data.logDevice.longitude));

      // Os mesmos 20 campos que a tela antiga montava - a diferenca e que aqui
      // eles sao exibidos.
      const p = data?.device?.entidade?.politicas?.[0] ?? {};
      const sn = (v: unknown) => (v ? 'Sim' : 'Não');
      setBloqueios([
        { label: 'Bluetooth', value: sn(p.bluetoothConfigDisabled) },
        { label: 'SMS', value: sn(p.smsDisabled) },
        { label: 'WIFI', value: sn(p.wifiConfigDisabled) },
        { label: 'Redefinição para a configuração original', value: sn(p.factoryResetDisabled) },
        { label: 'Status Bar', value: sn(p.statusBarDisabled) },
        { label: 'Opção mudar localização desativado', value: sn(p.shareLocationDisabled) },
        { label: 'Compartilhamento de contatos via Bluetooth', value: sn(p.bluetoothContactSharingDisabled) },
        { label: 'Adicionar ou remover contas está desativado', value: sn(p.modifyAccountsDisabled) },
        { label: 'Usuário que monta mídia externa física', value: sn(p.mountPhysicalMediaDisabled) },
        { label: 'Configuração de credenciais do usuário', value: sn(p.credentialsConfigDisabled) },
        { label: 'Redefinição das configurações de rede desativada', value: sn(p.networkResetDisabled) },
        { label: 'Adicionar novos usuários e perfis', value: sn(p.addUserDisabled) },
        { label: 'Desinstalação de aplicativos pelo usuário desativada', value: sn(p.uninstallAppsDisabled) },
        { label: 'Configuração de redes móveis', value: sn(p.mobileNetworksConfigDisabled) },
        { label: 'Captura de tela desativada', value: sn(p.screenCaptureDisabled) },
        { label: 'Ajuste do volume mestre desativado', value: sn(p.adjustVolumeDisabled) },
        { label: 'NFC para enviar dados de apps desativado', value: sn(p.outgoingBeamDisabled) },
        { label: 'Chamadas realizadas desativadas', value: sn(p.outgoingCallsDisabled) },
        { label: 'Remoção de outros usuários desativada', value: sn(p.removeUserDisabled) },
        { label: 'Configuração da VPN', value: sn(p.vpnConfigDisabled) },
        { label: 'Criação de janelas além das do app', value: sn(p.createWindowsDisabled) },
      ]);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Erro em carregar os dados');
    } finally {
      setCarregando(false);
    }
  }, [deviceId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!deviceId) return;
    axios
      .get<any>(`/dispositivos/getConsumosDispositivosAplicativos/${encodeURIComponent(deviceId)}`)
      .then((r) => setConsumos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setConsumos([]));

    axios
      .get<any>(`/dispositivos/deviceReportAplications?deviceId=${encodeURIComponent(deviceId)}`)
      .then((r) => setRelatorio(r.data?.content ?? []))
      .catch(() => setRelatorio([]));
  }, [deviceId]);

  /** Erro do backend em uma linha - o formato de erro do projeto tem 3 formas. */
  function mensagemDeErro(err: any, padrao = 'Ops ocorreu um erro no sistema.') {
    const e = err?.response?.data;
    if (e?.errors?.length > 0) return e.errors[0]?.message || padrao;
    return e?.message || padrao;
  }

  /* "ENVIADO", E NAO "EXECUTADO" (29/09/2026).

     As mensagens diziam "Acao executada com sucesso" - e "verifique o telefone
     tocando". Mas 200 do `issueCommand` do Android Management significa que o
     comando ENTROU NA FILA do Google, nao que o aparelho o executou. Um aparelho
     offline ha dias recebe o mesmo 200.

     O operador lia "executado", concluia que o aparelho reiniciou ou tocou, e
     parava de investigar - justamente no caso em que o aparelho esta sumido e
     o comando nunca vai chegar.

     Agora a mensagem diz o que de fato aconteceu (o comando foi enviado) e a
     condicao para o efeito (o aparelho estar online). */
  async function acao(nome: string, executar: () => Promise<any>, sucesso: string) {
    setOcupado(nome);
    try {
      const r = await executar();
      if (r?.status === 200 || r?.status === 201) enqueueSnackbar(sucesso, { variant: 'success', style: { whiteSpace: 'pre-line' } });
      return r;
    } catch (err: any) {
      enqueueSnackbar(mensagemDeErro(err), { variant: 'error', style: { whiteSpace: 'pre-line' } });
      return null;
    } finally {
      setOcupado('');
    }
  }

  const sincronizar = () =>
    acao(
      'sinc',
      () => axios.get(`/dispositivos/sincronizacao/${encodeURIComponent(deviceId)}`),
      'Sincronização enviada. O aparelho atualiza quando estiver online.'
    );

  const tocar = () =>
    acao(
      'tocar',
      () => axios.get(`/dispositivos/tocar/${encodeURIComponent(deviceId)}`),
      'Comando enviado. Se o aparelho estiver online, ele toca em alguns segundos.'
    );

  async function ultimaLocalizacao() {
    const r: any = await acao(
      'local',
      () => axios.get(`/dispositivos/ultimaLocalizacao/${encodeURIComponent(deviceId)}`),
      'Ação executada com sucesso'
    );
    if (!r?.data?.logDevice) return;
    const l = r.data.logDevice;
    setLogDevice((v: any) => ({ ...v, latitude: l.latitude, longitude: l.longitude }));
    if (l.latitude) setLat(Number(l.latitude));
    if (l.longitude) setLng(Number(l.longitude));
    enqueueSnackbar(`A última localização foi às ${fDateSimple(l.createdAt)}`, { variant: 'info' });
  }

  async function reiniciar() {
    await acao(
      'reboot',
      () => axios.get(`/dispositivos/reboot?deviceId=${encodeURIComponent(deviceId)}`),
      'Reinício enviado. O aparelho reinicia quando receber o comando — se estiver offline, só ao voltar.'
    );
    setConfirmarReiniciar(false);
  }

  async function resetDeFabrica() {
    setOcupado('reset');
    try {
      await axios.delete(`/dispositivos/${encodeURIComponent(deviceId)}`);
      enqueueSnackbar('Dispositivo apagado com sucesso.', { variant: 'success' });
      setConfirmarReset(false);
      // Sem o aparelho nao ha detalhe para mostrar - volta para a listagem.
      router.push(`/dashboard/dispositivos-novo/${grupoId}`);
    } catch (err: any) {
      enqueueSnackbar(mensagemDeErro(err), { variant: 'error', style: { whiteSpace: 'pre-line' } });
    } finally {
      setOcupado('');
    }
  }

  async function enviarRecadastro() {
    await acao(
      'recad',
      () => axios.post(`/dispositivos/recadastrar/${encodeURIComponent(deviceId)}`, { recadastroSenha: senhaRecadastro }),
      'Recadastro enviado. Se o aparelho estiver online, a tela de cadastro aparece nele.'
    );
    setRecadastrar(false);
    setSenhaRecadastro('');
  }

  async function confirmarLimparApp() {
    if (!limparApp?.packageName) return;
    setOcupado('clear');
    try {
      await axios.post('/dispositivos/clearCrash', { deviceId, packageName: limparApp.packageName });
      enqueueSnackbar(`Dados do ${limparApp.packageName} limpo com sucesso.`, { variant: 'success' });
      setLimparApp(null);
    } catch (err: any) {
      enqueueSnackbar(mensagemDeErro(err), { variant: 'error', style: { whiteSpace: 'pre-line' } });
    } finally {
      setOcupado('');
    }
  }

  async function copiar(valor?: string) {
    if (!valor) return;
    try {
      await navigator.clipboard.writeText(valor);
      enqueueSnackbar('Texto copiado com sucesso.', { variant: 'success' });
    } catch {
      // Fora de contexto seguro o clipboard nao existe; o texto continua na tela
      // e selecionavel, entao nao vale mostrar erro.
    }
  }

  const politica = device?.entidade?.politicas?.[0];
  const temLocal = Boolean(logDevice?.latitude && logDevice?.longitude);

  const secoesDetalhe = useMemo(
    () => [
      {
        titulo: 'Identificação',
        campos: [
          ['Device ID', device?.deviceId],
          ['Nome', device?.nome],
          ['CPF', device?.cpf],
          ['CR', device?.cr?.descricaoCompleta],
          ['Última sincronização', logDevice?.createdAt ? new Date(logDevice.createdAt).toLocaleString('pt-BR') : null],
        ],
      },
      {
        titulo: 'Hardware',
        campos: [
          ['Marca', device?.hardwareInfo_manufacturer],
          ['Modelo', device?.hardwareInfo_model],
          ['Serial Number', device?.hardwareInfo_serialNumber],
          ['Bateria', logDevice?.bateria != null ? `${logDevice.bateria}%` : null],
          ['Memória RAM', device?.memoryInfo_totalRam ? bytes(device.memoryInfo_totalRam) : null],
          ['Armazenamento', device?.external_storage_detected ? bytes(device.external_storage_detected) : null],
        ],
      },
      {
        titulo: 'Software',
        campos: [
          ['Sistema Operacional', device?.softwareInfo_androidVersion ? `Android ${device.softwareInfo_androidVersion}` : null],
          ['Patch de Segurança', device?.softwareInfo_securityPatchLevel],
          ['Versão do Hub', logDevice?.versaoHub ?? device?.versaoHub],
        ],
      },
      {
        titulo: 'Rede',
        campos: [
          ['IMEI', device?.networkInfo_imei],
          ['MAC', device?.networkInfo_wifiMacAddress],
          ['Telefone 1', device?.networkInfo_telephonyInfos_phoneNumber_1],
          ['Telefone 2', device?.networkInfo_telephonyInfos_phoneNumber_2],
          ['Operadora 1', device?.networkInfo_telephonyInfos_carrierName_1],
          ['Operadora 2', device?.networkInfo_telephonyInfos_carrierName_2],
          ['Sinal Telefone', logDevice?.sinalTelefone],
          ['Sinal Dados Móveis', logDevice?.sinalDadosMoveis ?? logDevice?.sinal_dados_moveis],
          ['WIFI', logDevice?.wifi != null ? (logDevice.wifi ? 'Sim' : 'Não') : null],
        ],
      },
      {
        titulo: 'Uso e Localização',
        campos: [
          ['Endereço', logDevice?.endereco],
          ['Consumo WIFI', logDevice?.consumo_wifi],
          ['Consumo Dados Móveis', logDevice?.consumo_dados_moveis],
          ['Grupo', device?.entidade?.nome],
          ['Política', device?.policyName],
        ],
      },
    ],
    [device, logDevice]
  );

  if (carregando) {
    return (
      <Page title="Dispositivo">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2">
          <div className="h-9 w-64 animate-pulse rounded bg-soft" />
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg bg-soft" />
            ))}
          </div>
          <div className="mt-4 h-[420px] animate-pulse rounded-lg bg-soft" />
        </div>
      </Page>
    );
  }

  if (erro || !device) {
    return (
      <Page title="Dispositivo">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2">
          <div className="rounded-lg border border-line bg-danger-soft p-4 text-[12.5px] text-danger">
            {erro || 'Dispositivo não encontrado.'}
            <button type="button" onClick={carregar} className="ml-2 font-semibold underline underline-offset-2">
              tentar de novo
            </button>
          </div>
        </div>
      </Page>
    );
  }

  /* O `tom` de cada cartao (10/09/2026, junto com a barra colorida).

     Nao e enfeite: a cor separa o que e MEDIDA do que e ESTADO. Aplicativos,
     armazenamento e RAM sao contagens - `info`, sempre o mesmo tom, porque nao
     ha bom nem ruim num numero de apps instalados. Bateria e localizacao sao
     estado, e mudam de tom conforme o valor: bateria baixa e localizacao
     ausente sao as duas coisas que fazem alguem abrir esta tela as pressas.
     Politica sem vinculo tambem e aviso - aparelho sem politica nao recebe
     regra nenhuma. */
  /* Compara com TODOS os campos da linha, e nao so com o nome: aqui se procura
     tanto por "gps" quanto por "br.com.gpssa" quanto por versao. */
  function filtrarPorBusca<T>(lista: T[], campos: (item: T) => Array<unknown>): T[] {
    const alvo = semAcento(buscaAba);
    if (!alvo) return lista;
    const termos = alvo.split(/\s+/);
    return lista.filter((item) => {
      const linha = semAcento(campos(item).filter((v) => v != null && v !== '').join(' '));
      return termos.every((t) => linha.includes(t));
    });
  }

  /* OS SEIS CARTOES (10/09/2026, ajustado a pedido).

     Tres mudancas com o mesmo motivo - cada cartao tem que carregar um DADO, e
     nao um estado que ja esta dito em outro lugar:

     - LOCALIZACAO saiu. Ela dizia "Sim"/"Nao", e logo abaixo ha um mapa com o
       endereco: um cartao inteiro para repetir o que a figura mostra. No lugar
       entrou GRUPO, que era pergunta frequente e so aparecia no fim da ficha.

     - POLITICA mostrava "vinculada". A pergunta nunca e "tem politica?", e
       "QUAL politica?" - trocar por regra que o aparelho recebe exige saber o
       nome. Agora o nome e o valor, e a regional vira a legenda.

     - ICONE em cada um. Sao seis cartoes iguais em forma; o icone e o que
       permite achar bateria sem ler os seis rotulos.

     O `tom` continua separando MEDIDA de ESTADO: contagem em `info`, e bateria
     mudando de cor porque bateria baixa e o que faz alguem abrir esta tela as
     pressas. */
  /* MEDIDA E IDENTIDADE PASSARAM A TER TRATAMENTOS DIFERENTES (29/09/2026,
     relatado: "melhorar os cards de cima, tem texto cortado e muito grande").

     Os seis cartoes usavam o mesmo `text-2xl font-extrabold`. Funciona para
     "19" e "100%"; para "GERENTE FABIO" e "_GERENTE_FABIO" o nome quebrava em
     duas linhas, empurrava o cartao e comia o espaco da legenda - que aparecia
     como "a que pert..." e "R...".

     `texto` muda o tratamento para nome; `href` transforma Grupo e Politica em
     LINK, que era o pedido do suporte ("na tela de detalhe nao tem mais o link
     para a politica do aparelho"). Sao as duas unicas informacoes da faixa que
     levam a outra tela. */
  const cartoes: Array<{
    rotulo: string;
    valor: string;
    legenda?: string;
    tom: 'info' | 'ok' | 'warn' | 'danger' | 'neutro';
    texto?: boolean;
    href?: string;
    icone: React.ReactNode;
  }> = [
    {
      rotulo: 'Aplicativos',
      valor: String(aplicativos.length ?? 0),
      legenda: 'instalados',
      tom: 'info',
      icone: <Glifo d="M3 3h4.5v4.5H3V3z M10.5 3H15v4.5h-4.5V3z M3 10.5h4.5V15H3v-4.5z M10.5 10.5H15V15h-4.5v-4.5z" />,
    },
    {
      rotulo: 'Bateria',
      valor: logDevice?.bateria != null ? `${logDevice.bateria}%` : '—',
      legenda: 'último log',
      tom: logDevice?.bateria == null ? 'info' : Number(logDevice.bateria) <= 20 ? 'danger' : 'ok',
      icone: <Glifo d="M2.5 6h10v6h-10V6z M14 8v2 M4.5 8v2" />,
    },
    {
      /* Sem dado deixa de ser um travessao em 22px - o mesmo destaque de "100%"
         para dizer que NAO se sabe. Vira texto cinza com o motivo na legenda. */
      rotulo: 'Armazenamento',
      valor: device.internal_storage_detected ? bytes(device.internal_storage_detected) : 'sem dado',
      legenda: device.internal_storage_detected
        ? `de ${bytes(device.external_storage_detected)}`
        : 'o aparelho não reportou',
      texto: !device.internal_storage_detected,
      tom: device.internal_storage_detected ? 'info' : 'neutro',
      icone: <Glifo d="M2.5 4.5h13v9h-13v-9z M2.5 9h13 M5 6.6h.01 M5 11.4h.01" />,
    },
    {
      rotulo: 'Memória RAM',
      valor: device.memoryInfo_totalInternalStorage
        ? bytes(device.memoryInfo_totalInternalStorage)
        : 'sem dado',
      legenda: device.memoryInfo_totalRam
        ? `de ${bytes(device.memoryInfo_totalRam)}`
        : 'o aparelho não reportou',
      texto: !device.memoryInfo_totalInternalStorage,
      tom: device.memoryInfo_totalInternalStorage ? 'info' : 'neutro',
      icone: <Glifo d="M4 5h10v8H4V5z M6.5 5V2.8 M11.5 5V2.8 M6.5 15.2V13 M11.5 15.2V13" />,
    },
    {
      rotulo: 'Grupo',
      valor: texto(device.entidade?.nome),
      legenda: device.entidade?.codigo ? 'abrir grupo' : 'sem grupo',
      texto: true,
      href: device.entidade?.codigo
        ? `/dashboard/entidades-novo?editar=${device.entidade.codigo}`
        : undefined,
      tom: 'info',
      icone: <Glifo d="M3.5 15.5V4l5-1.8v13.3 M8.5 6.6l6 1.6v7.3 M3 15.5h12 M5.6 6.4h.01 M5.6 9.2h.01 M5.6 12h.01" />,
    },
    {
      rotulo: 'Política',
      valor: politica?.name ? String(politica.name) : 'sem política',
      legenda: politica?.name ? 'abrir política' : 'nenhuma regra chega ao aparelho',
      texto: true,
      /* Rota `politicas-novo/new/<name>`: a antiga (`politicas/new/...`) foi
         removida em 19/08 e dava 404. Mesma rota usada no modal de grupo. */
      href: politica?.name
        ? `/dashboard/politicas-novo/new/${encodeURIComponent(String(politica.name))}`
        : undefined,
      tom: politica?.name ? 'info' : 'warn',
      icone: <Glifo d="M9 2.4l5.4 2.2v4.2c0 3.4-2.4 5.6-5.4 6.8-3-1.2-5.4-3.4-5.4-6.8V4.6L9 2.4z M6.8 9l1.6 1.6 3-3.2" />,
    },
  ];

  return (
    <Page title="Dispositivo">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <nav className="mb-1 flex items-center gap-1.5 text-[11px] text-faint">
              <NextLink href="/dashboard/app">Home</NextLink>
              <span>/</span>
              <NextLink href={`/dashboard/dispositivos-novo/${grupoId}`}>Dispositivos</NextLink>
              <span>/</span>
              <span className="text-muted-foreground">detalhe</span>
            </nav>

            {/* ROTULO NOS DOIS VALORES (10/09/2026, a pedido).

                O topo mostrava duas coisas sem dizer o que eram: um texto grande
                ("SS A06", ou um numero de patrimonio como "0411525") e uma
                sequencia hexadecimal. Quem abre a tela pela primeira vez nao tem
                como saber que a primeira e o nome que ALGUEM digitou e a segunda
                e o identificador que o Google gerou - e sao usadas em conversas
                diferentes: o nome ao falar com quem esta com o aparelho, o ID ao
                abrir chamado.

                Os rotulos ficam na MESMA linha dos valores, e nao em linhas
                proprias: o pedido veio junto de "nao fique muita informacao", e
                o topo ja tem trilha, titulo, identificador e cinco botoes. */}
            <div className="flex flex-wrap items-baseline gap-x-2">
              <h1 className="block truncate text-2xl font-extrabold tracking-[-0.03em]">{texto(device.nome)}</h1>
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-faint">
                nome / patrimônio
              </span>

              {/* ESTADO E REGIONAL NA LINHA DO NOME (10/09/2026, desenho aprovado).

                  As duas perguntas que vinham antes de qualquer outra - "esse
                  aparelho esta reportando?" e "de quem e?" - exigiam descer ate
                  o cartao de localizacao e ate a aba de informacoes.

                  O criterio de "online" e o mesmo do cartao "Ultimo historico
                  recebido" logo abaixo: chegou log ha menos de 15 minutos. Nao
                  invento um segundo criterio - duas definicoes de online no
                  mesmo produto acabam discordando na tela. */}
              {(() => {
                const t = new Date(logDevice?.createdAt ?? '').getTime();
                if (!Number.isFinite(t)) return null;
                const minutos = Math.round((Date.now() - t) / 60000);
                const online = minutos <= 15;
                return (
                  <span
                    className={`inline-flex flex-none items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${
                      online ? 'bg-ok-soft text-ok' : 'bg-soft text-muted-foreground'
                    }`}
                    title={`Último log em ${fDateSimple(logDevice?.createdAt) || '—'}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-ok' : 'bg-faint'}`} />
                    {online ? 'Online' : minutos < 60 ? `há ${minutos} min` : 'Sem contato recente'}
                  </span>
                );
              })()}

              {politica?.regional?.nome && (
                <span className="inline-flex flex-none items-center rounded-full bg-soft px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                  {politica.regional.nome}
                </span>
              )}

              {/* MODO PERDIDO SEM MENSAGEM (29/09/2026). Ate hoje o comando aceitava
                  todos os campos vazios: o Google travava o aparelho com a tela de
                  bloqueio EM BRANCO - quem achasse nao sabia a quem devolver
                  (caso do 39c57094472d8740, desde 14/09). A API agora exige ao menos
                  um campo; este aviso acha os que ja ficaram assim. */}
              {(Number(device?.status) === 2 || device?.status === 'PERDIDO') && (
                <span
                  className="inline-flex flex-none items-center rounded-full bg-danger-soft px-2 py-0.5 text-[10.5px] font-bold text-danger"
                  title={
                    device?.perdaMensagem || device?.perdaTelefone || device?.perdaEmail || device?.perdaEndereco
                      ? 'Aparelho em modo perdido.'
                      : 'A tela de bloqueio está sem mensagem, telefone e e-mail. Reenvie o modo perdido preenchendo ao menos um campo.'
                  }
                >
                  {device?.perdaMensagem || device?.perdaTelefone || device?.perdaEmail || device?.perdaEndereco
                    ? 'Modo perdido'
                    : 'Modo perdido — tela de bloqueio sem mensagem'}
                </span>
              )}
            </div>

            <div className="mt-1 flex items-center gap-2">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-faint">device id</span>
              <Num className="text-[12px] text-muted-foreground">{texto(device.deviceId)}</Num>
              <button
                type="button"
                onClick={() => copiar(device.deviceId)}
                className="h-6 rounded border border-line px-1.5 text-[11px] font-semibold text-muted-foreground hover:text-ink"
                title="Copiar DEVICE_ID"
              >
                copiar
              </button>
            </div>
          </div>

          {podeAgir && (
            /* BARRA DE ACOES ENXUTA (10/09/2026, desenho aprovado).

               Eram DOZE botoes numa fila so, do mesmo tamanho e do mesmo peso:
               Sincronizar, Ultima localizacao, Tocar, Recadastrar, Editar,
               Mudar grupo, Mudar senha, Geolocalizacao, Logs, Perda/Roubo,
               Reiniciar e Reset de fabrica. Doze alvos iguais nao formam uma
               barra de acoes, formam um paredao - e "Reset de fabrica" ficava
               a 12px de "Logs", que e leitura.

               Agora ha hierarquia:
                 - SINCRONIZAR e primaria (pintada). E a acao que se repete o
                   dia inteiro no suporte: forcar o aparelho a reportar.
                 - EDITAR e LOCALIZAR ficam a vista, com moldura.
                 - o resto entra em "Mais acoes", inclusive as destrutivas, que
                   ali aparecem em vermelho depois de um separador (o MenuAcoes
                   ja faz isso com `perigo`).

               As destrutivas continuam pedindo confirmacao em modal - o menu
               so muda onde elas ficam, nao o que elas exigem. */
            <div className="flex flex-none flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={sincronizar}
                disabled={Boolean(ocupado)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3.5 text-[12px] font-bold text-primary-foreground disabled:opacity-40"
              >
                <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden>
                  <path
                    d="M15 9a6 6 0 11-1.8-4.3 M15 2.6V6h-3.4"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {ocupado === 'sinc' ? 'Sincronizando...' : 'Sincronizar'}
              </button>

              <button
                type="button"
                onClick={() => setAbrirEditar(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
              >
                <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden>
                  <path
                    d="M3 15h12 M11.6 3.4a1.6 1.6 0 012.3 2.3L6.6 13H4.3v-2.3l7.3-7.3z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Editar
              </button>

              <button
                type="button"
                onClick={ultimaLocalizacao}
                disabled={Boolean(ocupado)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
                title="Pede ao aparelho a posição atual"
              >
                <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden>
                  <path
                    d="M9 2.4c2.6 0 4.7 2.1 4.7 4.7 0 3.3-4.7 8.5-4.7 8.5S4.3 10.4 4.3 7.1C4.3 4.5 6.4 2.4 9 2.4z M9 5.6a1.6 1.6 0 100 3.2 1.6 1.6 0 000-3.2z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {ocupado === 'local' ? 'Buscando...' : 'Localizar'}
              </button>

              <MenuAcoes<{ id: string }>
                linha={{ id: deviceId }}
                aberto={menuAcoes}
                onAlternar={() => setMenuAcoes((v) => !v)}
                onFechar={() => setMenuAcoes(false)}
                ancorarADireita
                gatilho={{
                  aria: 'Mais ações',
                  className:
                    'inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink',
                  rotulo: (
                    <>
                      Mais ações
                      <svg width="11" height="11" viewBox="0 0 18 18" fill="none" aria-hidden>
                        <path d="M4 6.5L9 11.5l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  ),
                }}
                acoes={[
                  { rotulo: 'Tocar no aparelho', ao: () => tocar() },
                  {
                    rotulo: 'Recadastrar',
                    ao: () => {
                      setSenhaRecadastro(device.recadastroSenha ?? '');
                      setRecadastrar(true);
                    },
                  },
                  { rotulo: 'Mudar grupo', ao: () => setAbrirGrupo(true) },
                  { rotulo: 'Mudar senha', ao: () => setAbrirSenha(true) },
                  { rotulo: 'Geolocalização e trajeto', ao: () => setAbrirGeo(true) },
                  { rotulo: 'Logs', ao: () => setAbrirLogs(true) },
                  { rotulo: 'Perda / roubo', perigo: true, ao: () => setAbrirPerda(true) },
                  { rotulo: 'Reiniciar', perigo: true, ao: () => setConfirmarReiniciar(true) },
                  { rotulo: 'Reset de fábrica', perigo: true, ao: () => setConfirmarReset(true) },
                ]}
              />
            </div>
          )}
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {/* O MESMO CARTAO DO HOME (10/09/2026, a pedido da barra fina).

              Estes eram desenhados a mao aqui - moldura cinza, sem barra, sem
              tom - enquanto o Home usava `CartaoIndicador`. Duas versoes do
              mesmo objeto no mesmo produto envelhecem em direcoes diferentes:
              a barra de cor pedida ja existia pronta la, e refaze-la aqui
              criaria a terceira. Agora e o componente, e o que ele ganhar vale
              para as duas telas. */}
          {cartoes.map((c) => (
            <CartaoIndicador
              key={c.rotulo}
              rotulo={c.rotulo}
              valor={c.valor}
              legenda={c.legenda}
              tom={c.tom}
              texto={c.texto}
              href={c.href}
              icone={c.icone}
            />
          ))}
        </div>

        {/* A LINHA "Ultimo historico recebido" SAIU (10/09/2026, a pedido).

            Ela repetia, em texto pequeno e alinhado a direita, a mesma
            informacao que o selo ao lado do nome agora mostra ("Online" / "ha
            N min") - e que os cartoes de bateria e localizacao ja qualificam
            com "ultimo log". Tres lugares para o mesmo carimbo de tempo.
            A data exata continua no `title` do selo. */}

        <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
          {/* ABAS no padrao minimalista do resto do sistema: traco de 2px embaixo da
              selecionada, sem "pasta" (borda em cima e nos lados, fundo pintado) e
              sem o cinza na tira. `role="tablist"`/`aria-selected` para o leitor de
              tela anunciar que sao abas, e nao sete botoes soltos - faltava aqui. */}
          <div role="tablist" aria-label="Detalhe do dispositivo" className="flex gap-1 overflow-x-auto border-b border-line px-2">
            {ABAS.map((a) => (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={aba === a.id}
                onClick={() => setAba(a.id)}
                className={`-mb-px flex-none border-b-2 px-3 py-2 text-[12px] ${
                  aba === a.id
                    ? 'border-primary font-bold text-primary'
                    : 'border-transparent font-semibold text-muted-foreground hover:border-line hover:text-ink'
                }`}
              >
                {a.rotulo}
              </button>
            ))}
          </div>

          <div className="p-4">
            {aba === 'info' && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="flex flex-col">
                  <div className="mb-3 flex justify-center">
                    <img src="/images/celular.jpg" className="h-56 w-28 rounded-md object-cover" alt="Celular" />
                  </div>

                  <div className="mb-3 text-center">
                    <span className="block text-[12.5px] font-bold">Marca / Modelo</span>
                    <span className="block text-[12px] text-muted-foreground">
                      {texto(device.hardwareInfo_manufacturer)}
                    </span>
                    <span className="block text-[12px] text-muted-foreground">
                      {texto(device.hardwareInfo_model)}
                    </span>
                  </div>

                  <dl className="divide-y" style={{ borderColor: 'var(--gps-soft)' }}>
                    {[
                      ['Alteração', fDateSimple(device.updatedAt?.toString()), 'Data em que foram alteradas informações do dispositivo (nome, CR, CPF e outros)'],
                      ['Sistema Operacional', device.softwareInfo_androidVersion ? `Android ${device.softwareInfo_androidVersion}` : '—'],
                      ['Número de série', device.hardwareInfo_serialNumber, undefined, true],
                      ['IMEI', device.networkInfo_imei, undefined, true],
                      ['Bateria', logDevice?.bateria != null ? `${logDevice.bateria} %` : '—'],
                      ['CR', device.cr?.descricaoCompleta],
                      [
                        'Operadoras',
                        [device.networkInfo_telephonyInfos_carrierName_1, device.networkInfo_telephonyInfos_carrierName_2]
                          .filter(Boolean)
                          .join(', '),
                      ],
                      [
                        'Telefones',
                        [device.networkInfo_telephonyInfos_phoneNumber_1, device.networkInfo_telephonyInfos_phoneNumber_2]
                          .filter(Boolean)
                          .join(', '),
                      ],
                    ].map(([rotulo, valor, dica, mono]: any) => (
                      <div key={String(rotulo)} className="flex items-start justify-between gap-3 py-1.5">
                        <span className="text-[12px] font-semibold" title={dica}>
                          {rotulo}
                        </span>
                        {mono ? (
                          <Num className="text-right text-[12px] text-muted-foreground">{texto(valor)}</Num>
                        ) : (
                          <span className="text-right text-[12px] text-muted-foreground">{texto(valor)}</span>
                        )}
                      </div>
                    ))}

                    <div className="flex items-center justify-between gap-3 py-1.5">
                      <span className="text-[12px] font-semibold">Grupo</span>
                      {/* Aponta para a lista NOVA de grupos com o modal aberto
                          (`?editar=<codigo>`), e nao mais para
                          `/dashboard/entidades/new/:id`, que e a tela antiga em MUI. */}
                      <NextLink
                        href={`/dashboard/entidades-novo?editar=${device.entidade?.codigo}`}
                        className="text-right text-[12px] font-semibold text-primary hover:underline"
                      >
                        {texto(device.entidade?.nome)}
                      </NextLink>
                    </div>

                    <div className="flex items-center justify-between gap-3 py-1.5">
                      <span className="text-[12px] font-semibold">Política</span>
                      {politica?.name ? (
                        <NextLink
                          // Rota corrigida: a antiga (`politicas/new/...`) foi
                          // removida em 19/08 e este link dava 404.
                          href={`/dashboard/politicas-novo/new/${encodeURIComponent(politica.name)}`}
                          className="block truncate text-right text-[12px] font-semibold text-primary hover:underline"
                        >
                          {politica.name}
                        </NextLink>
                      ) : (
                        <span className="text-[12px] text-muted-foreground">—</span>
                      )}
                    </div>
                  </dl>
                </div>

                <div>
                  {!temLocal ? (
                    <div className="flex h-[420px] items-center justify-center rounded-lg border border-line bg-surface-2 text-[12.5px] text-muted-foreground">
                      Sem localização no dispositivo
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-line">
                      <GoogleMap
                        center={{ lat, lng }}
                        zoom={zoom}
                        setZoom={setZoom}
                        /* O MAPA ACOMPANHA A COLUNA DA ESQUERDA (10/09/2026,
                           a pedido). Eram 480px contra uma ficha de ~660px:
                           sobrava um vazio alto ao lado do mapa, e o mapa - que
                           e a unica coisa da direita - ficava sendo o elemento
                           pequeno de uma tela larga. `h-full` na moldura mais
                           100% aqui deixam a altura ser decidida pela ficha, que
                           e o conteudo que manda: se ela crescer, o mapa cresce
                           junto, sem numero novo para manter em dois lugares. */
                        style={{ width: '100%', height: '100%', minHeight: '480px' }}
                        disableDefaultUI
                        clickableIcons={false}
                      >
                        <GoogleMarker
                          position={{ lat, lng }}
                          draggable
                          setLat={setLat}
                          setLng={setLng}
                          setAddress={setAddress}
                        />
                      </GoogleMap>
                    </div>
                  )}

                  {logDevice?.endereco && (
                    <p className="mt-2 text-[12px] text-muted-foreground">
                      Endereço do último log: <span className="text-ink">{logDevice.endereco}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {aba === 'detalhes' && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {secoesDetalhe.map((secao) => (
                  <div key={secao.titulo} className="rounded-lg border border-line bg-surface-2 p-3.5">
                    <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[0.06em] text-info">
                      {secao.titulo}
                    </h3>
                    <div className="divide-y" style={{ borderColor: 'var(--gps-border)' }}>
                      {secao.campos.map(([rotulo, valor]: any) => (
                        <div key={String(rotulo)} className="flex items-start justify-between gap-3 py-1.5">
                          <span className="text-[12px] font-semibold">{String(rotulo)}</span>
                          <span className="break-words text-right text-[12px] text-muted-foreground">
                            {valor != null && valor !== '' ? String(valor) : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {aba === 'apps' && (
              <>
                <BuscaDaAba valor={buscaAba} aoMudar={setBuscaAba} total={aplicativos.length} />
              <TabelaSimples
                colunas={['Nome', 'Pacote', 'Tipo', 'Opções']}
                grade="minmax(180px,2fr) minmax(200px,2fr) 160px 110px"
                vazio="Nenhum aplicativo instalado pelo usuário."
                linhas={filtrarPorBusca(aplicativos, (i) => [i.name, i.packageName, TIPO_APLICATIVO[String(i.installType)]]).map((item) => [
                  <span className="block truncate text-[12px] font-semibold">{texto(item.name)}</span>,
                  <Num className="block truncate text-[11px] text-muted-foreground">{texto(item.packageName)}</Num>,
                  <span className="text-[12px] text-muted-foreground">
                    {TIPO_APLICATIVO[String(item.installType)] || '—'}
                  </span>,
                  <button
                    type="button"
                    onClick={() => setLimparApp(item)}
                    className="h-7 rounded-md border border-line px-2 text-[11px] font-semibold text-danger hover:bg-danger-soft"
                  >
                    Limpar dados
                  </button>,
                ])}
                />
              </>
            )}

            {aba === 'sistema' && (
              <>
                <BuscaDaAba valor={buscaAba} aoMudar={setBuscaAba} total={relatorio.length} />
              <TabelaSimples
                colunas={['Nome', 'Pacote', 'Nome da versão', 'Versão código']}
                grade="minmax(180px,2fr) minmax(200px,2fr) 150px 130px"
                vazio="Nenhum aplicativo reportado pelo aparelho."
                linhas={filtrarPorBusca(relatorio, (i) => [i.displayName, i.packageName, i.versionName, i.versionCode]).map((item) => [
                  <span className="block truncate text-[12px] font-semibold">{texto(item.displayName)}</span>,
                  <Num className="block truncate text-[11px] text-muted-foreground">{texto(item.packageName)}</Num>,
                  <span className="text-[12px] text-muted-foreground">{texto(item.versionName)}</span>,
                  <Num className="text-[12px] text-muted-foreground">{texto(item.versionCode)}</Num>,
                ])}
                />
              </>
            )}

            {aba === 'consumo' && (
              <>
                <BuscaDaAba valor={buscaAba} aoMudar={setBuscaAba} total={consumos.length} />
              <TabelaSimples
                colunas={['Nome', 'Pacote', 'Dados móveis', 'WIFI']}
                grade="minmax(180px,2fr) minmax(200px,2fr) 150px 150px"
                vazio="Nenhum consumo reportado por aplicativo."
                linhas={filtrarPorBusca(consumos, (i) => [i.nome, i.packageName]).map((item) => [
                  <span className="block truncate text-[12px] font-semibold">{texto(item.nome)}</span>,
                  <Num className="block truncate text-[11px] text-muted-foreground">{texto(item.packageName)}</Num>,
                  <Num className="text-[12px] text-muted-foreground">{bytes(item.consumo_dados_moveis)}</Num>,
                  <Num className="text-[12px] text-muted-foreground">{bytes(item.consumo_wifi)}</Num>,
                ])}
                />
              </>
            )}

            {aba === 'bloqueios' && (
              <>
                <p className="mb-3 max-w-[80ch] text-[12px] text-muted-foreground">
                  O que a política <strong>{texto(politica?.name)}</strong> bloqueia neste aparelho.{' '}
                  <em>Sim</em> significa <strong>desativado para o usuário</strong>. Estes dados já vinham na
                  resposta da API e não eram exibidos em nenhum lugar.
                </p>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                  {bloqueios.map((b) => (
                    <div
                      key={b.label}
                      className="flex items-start justify-between gap-3 border-b py-1.5"
                      style={{ borderColor: 'var(--gps-soft)' }}
                    >
                      <span className="text-[12px]">{b.label}</span>
                      <span
                        className={`flex-none rounded px-1.5 text-[11px] font-bold ${
                          b.value === 'Sim' ? 'bg-danger-soft text-danger' : 'bg-soft text-muted-foreground'
                        }`}
                      >
                        {b.value}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {aba === 'historico' && device.deviceId && (
              <HistoricoGPS
                tipo={'DISPOSITIVO'}
                chave={device.networkInfo_imei || ''}
                chaveSecundaria={device.hardwareInfo_serialNumber || ''}
              />
            )}
          </div>
        </div>
      </div>

      <Modal
        aberto={confirmarReiniciar}
        titulo="Reiniciar o dispositivo?"
        descricao="O aparelho desliga e liga. Quem estiver usando perde o que não salvou."
        icone="⏻"
        largura="max-w-[460px]"
        onFechar={() => setConfirmarReiniciar(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmarReiniciar(false)} disabled={Boolean(ocupado)}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={reiniciar}
              disabled={Boolean(ocupado)}
              className="h-9 rounded-lg bg-warn px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {ocupado === 'reboot' ? 'Reiniciando...' : 'Reiniciar'}
            </button>
          </>
        }
      >
        <ResumoExclusao titulo={texto(device.nome)} detalhe={texto(device.deviceId)} />
      </Modal>

      <Modal
        aberto={confirmarReset}
        titulo="Reset de fábrica?"
        descricao="Apaga o aparelho e o remove do inventário. Não tem desfazer — ele precisa ser reprovisionado."
        icone="⚠"
        largura="max-w-[500px]"
        onFechar={() => setConfirmarReset(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmarReset(false)} disabled={Boolean(ocupado)}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={resetDeFabrica}
              disabled={Boolean(ocupado)}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {ocupado === 'reset' ? 'Executando...' : 'Fazer reset de fábrica'}
            </button>
          </>
        }
      >
        <ResumoExclusao
          titulo={texto(device.nome)}
          detalhe={texto(device.deviceId)}
          extra={
            <>
              Grupo: <strong className="text-ink">{texto(device.entidade?.nome)}</strong> · IMEI:{' '}
              <Num className="text-ink">{texto(device.networkInfo_imei)}</Num>
            </>
          }
        />
      </Modal>

      <Modal
        aberto={recadastrar}
        titulo="Recadastrar dispositivo"
        descricao="O aparelho volta a mostrar a tela de cadastro."
        icone="✎"
        largura="max-w-[460px]"
        onFechar={() => setRecadastrar(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setRecadastrar(false)} disabled={Boolean(ocupado)}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario onClick={enviarRecadastro} disabled={Boolean(ocupado)}>
              {ocupado === 'recad' ? 'Enviando...' : 'Enviar'}
            </BotaoPrimario>
          </>
        }
      >
        <Campo rotulo="Senha para bloqueio" dica="Opcional. Deixe em branco para não exigir senha.">
          <input
            value={senhaRecadastro}
            onChange={(e) => setSenhaRecadastro(e.target.value)}
            autoCapitalize="off"
            className={classesEntrada}
          />
        </Campo>
      </Modal>

      <Modal
        aberto={Boolean(limparApp)}
        titulo="Limpar dados do aplicativo?"
        descricao="O app volta ao estado de recém-instalado no aparelho. Dados locais dele são perdidos."
        icone="!"
        largura="max-w-[480px]"
        onFechar={() => setLimparApp(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setLimparApp(null)} disabled={Boolean(ocupado)}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={confirmarLimparApp}
              disabled={Boolean(ocupado)}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {ocupado === 'clear' ? 'Limpando...' : 'Limpar dados'}
            </button>
          </>
        }
      >
        {limparApp && <ResumoExclusao titulo={texto(limparApp.name)} detalhe={texto(limparApp.packageName)} />}
      </Modal>

      {/* OS MESMOS modais da listagem - nenhum componente foi duplicado.
          `device={device}` passa o registro que esta na tela; `onSalvo={carregar}`
          porque o que eles gravam (nome, CR, grupo, senha, perda) aparece aqui, e
          sem recarregar a tela mostraria o valor antigo logo depois de salvar. */}
      <ModalEditarDispositivo
        aberto={abrirEditar}
        device={device}
        onFechar={() => setAbrirEditar(false)}
        onSalvo={carregar}
      />
      <ModalMudarGrupo aberto={abrirGrupo} device={device} onFechar={() => setAbrirGrupo(false)} onSalvo={carregar} />
      <ModalMudarSenha aberto={abrirSenha} device={device} onFechar={() => setAbrirSenha(false)} onSalvo={carregar} />
      <ModalPerdaRoubo aberto={abrirPerda} device={device} onFechar={() => setAbrirPerda(false)} onSalvo={carregar} />
      <ModalGeolocalizacao aberto={abrirGeo} device={device} onFechar={() => setAbrirGeo(false)} />
      <ModalHistoricoDispositivo aberto={abrirLogs} device={device} onFechar={() => setAbrirLogs(false)} />
    </Page>
  );
}

/* ----------------------------------------------------------------------------
   Tabela das abas de aplicativo. As tres abas sao a mesma tabela com colunas
   diferentes - e a versao antiga tinha um defeito de layout que vale registrar:
   ela abria UM grid de 4 colunas e jogava todas as celulas de todas as linhas
   dentro, sem elemento por linha. Com isso nao havia como separar linha, dar
   hover ou zebrar - e uma celula vazia desalinhava a tabela inteira dali para
   baixo. Aqui cada linha e um elemento.
   ---------------------------------------------------------------------------- */
/* Campo de busca das abas de lista do dispositivo.

   Fica em cima da tabela e nao na barra de abas: ele pertence a LISTA que esta
   embaixo, e a barra de abas e navegacao. Posto la, pareceria filtrar as abas.

   Mostra "N de M" so quando ha filtro - sem filtro os dois numeros sao iguais e
   repetir isso e ruido. */
function BuscaDaAba({
  valor,
  aoMudar,
  total,
}: {
  valor: string;
  aoMudar: (v: string) => void;
  total: number;
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <label className="relative flex items-center">
        <svg
          width="13"
          height="13"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden
          className="pointer-events-none absolute left-2 text-faint"
        >
          <path d="M8 2.5a5.5 5.5 0 104 9.3l3.4 3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <input
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          placeholder="Pesquisar nesta lista"
          aria-label="Pesquisar nesta lista"
          className="h-7 w-60 rounded-md border border-line bg-surface pl-7 pr-6 text-[11px] outline-none focus:border-primary"
        />
        {valor && (
          <button
            type="button"
            onClick={() => aoMudar('')}
            aria-label="Limpar pesquisa"
            className="absolute right-1.5 text-[12px] text-faint hover:text-ink"
          >
            ✕
          </button>
        )}
      </label>
      <span className="text-[11px] text-muted-foreground">
        <Num className="font-semibold text-ink">{total}</Num> item(ns)
      </span>
    </div>
  );
}

function TabelaSimples({
  colunas,
  grade,
  linhas,
  vazio,
}: {
  colunas: string[];
  grade: string;
  linhas: React.ReactNode[][];
  vazio: string;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-line">
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          <div
            className={CABECALHO_TABELA_COMPACTO}
            style={{ gridTemplateColumns: grade }}
          >
            {colunas.map((c) => (
              <div key={c}>{c}</div>
            ))}
          </div>

          <div className="max-h-[46vh] overflow-y-auto">
            {!linhas.length && <div className="p-6 text-center text-[12px] text-muted-foreground">{vazio}</div>}

            {linhas.map((celulas, i) => (
              <div
                key={i}
                className="grid items-center border-b px-3 py-1.5 hover:bg-surface-2"
                style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)' }}
              >
                {celulas.map((celula, j) => (
                  <div key={j} className="min-w-0 pr-2">
                    {celula}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
